import { useState } from 'react';
import { X } from 'lucide-react';

interface InviteAdminModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const InviteAdminModal = ({ isOpen, onClose }: InviteAdminModalProps) => {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    role: 'Super Admin',
  });

  if (!isOpen) return null;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const getRoleDescription = (role: string) => {
    const descriptions: Record<string, string> = {
      'Super Admin': 'Full access to all platform modules, including sensitive financial data, system settings, and user management controls.',
      'Operations Manager': 'Access to operations, dispatching, and service management tools.',
      'Finance Admin': 'Access to financial reports, payouts, and billing management.',
      'Compliance Reviewer': 'Access to compliance checks and risk assessment tools.',
    };
    return descriptions[role] || '';
  };

  return (
    <>
      {isOpen && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-lg w-full max-w-md p-6 relative shadow-2xl my-8">
            {/* Close Button */}
            <button
              onClick={onClose}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 bg-transparent border-0 cursor-pointer"
            >
              <X className="w-5 h-5" />
            </button>

            {/* Icon */}
            <div className="flex justify-center mb-4">
              <div className="w-10 h-10 bg-teal-100 rounded-full flex items-center justify-center">
                <span className="text-lg">▶</span>
              </div>
            </div>

            {/* Title */}
            <h2 className="text-xl font-bold text-gray-900 mb-1">Invite New Administrator</h2>
            <p className="text-xs text-gray-600 mb-4">
              Admin will receive an email to set their password and complete their profile.
            </p>

            {/* Form */}
            <div className="space-y-4">
              {/* Full Name */}
              <div>
                <label className="block text-xs uppercase font-bold text-gray-700 tracking-wider mb-1.5">Full Name *</label>
                <input
                  type="text"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleChange}
                  placeholder="e.g. Julian Casablancas"
                  className="w-full px-3 py-2 bg-gray-100 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:bg-white transition-colors"
                />
              </div>

              {/* Email Address */}
              <div>
                <label className="block text-xs uppercase font-bold text-gray-700 tracking-wider mb-1.5">Email Address *</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="admin@virewire.co.uk"
                  className="w-full px-3 py-2 bg-gray-100 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:bg-white transition-colors"
                />
              </div>

              {/* Phone Number */}
              <div>
                <label className="block text-xs uppercase font-bold text-gray-700 tracking-wider mb-1.5">Phone Number (Optional)</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="+1 (555) 000-0000"
                  className="w-full px-3 py-2 bg-gray-100 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:bg-white transition-colors"
                />
              </div>

              {/* Assign Platform Role */}
              <div>
                <label className="block text-xs uppercase font-bold text-gray-700 tracking-wider mb-1.5">Assign Platform Role</label>
                <select
                  name="role"
                  value={formData.role}
                  onChange={handleChange}
                  className="w-full px-3 py-2 bg-gray-100 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:bg-white appearance-none cursor-pointer transition-colors"
                >
                  <option>Super Admin</option>
                  <option>Operations Manager</option>
                  <option>Finance Admin</option>
                  <option>Compliance Reviewer</option>
                </select>
              </div>

              {/* Role Description */}
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 flex gap-2">
                <span className="text-teal-600 flex-shrink-0 text-base mt-0">ⓘ</span>
                <div>
                  <p className="font-semibold text-xs text-gray-900 mb-0.5">{formData.role}</p>
                  <p className="text-xs text-gray-600 leading-tight">{getRoleDescription(formData.role)}</p>
                </div>
              </div>
            </div>

            {/* Buttons */}
            <div className="flex gap-3 mt-5 pt-3">
              <button
                onClick={() => {
                  // Handle send invitation
                  setFormData({
                    fullName: '',
                    email: '',
                    phone: '',
                    role: 'Super Admin',
                  });
                  onClose();
                }}
                className="flex-1 bg-teal-700 hover:bg-teal-800 text-white font-semibold py-2.5 rounded-lg transition-colors text-sm"
              >
                Send Invitation
              </button>
              <button
                onClick={onClose}
                className="flex-1 bg-white border border-gray-300 hover:bg-gray-50 text-gray-900 font-semibold py-2.5 rounded-lg transition-colors text-sm"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default InviteAdminModal;
